import AsyncStorage from '@react-native-async-storage/async-storage';

// キャッシュのキー
const CACHE_KEYS = {
  VIDEO_FEED: 'cache_video_feed_',
  VIDEO_POST: 'cache_video_post_',
  PROFILE: 'cache_profile_',
  POST_THREAD: 'cache_post_thread_',
} as const;

// キャッシュの有効期限（ミリ秒）
const CACHE_DURATION = {
  VIDEO_FEED: 5 * 60 * 1000, // 5分
  VIDEO_POST: 10 * 60 * 1000, // 10分
  PROFILE: 15 * 60 * 1000, // 15分
  POST_THREAD: 5 * 60 * 1000, // 5分
};

interface CacheData<T> {
  data: T;
  timestamp: number;
}

/**
 * キャッシュにデータを保存
 */
export const setCache = async <T>(key: string, data: T): Promise<void> => {
  try {
    const cacheData: CacheData<T> = {
      data,
      timestamp: Date.now(),
    };
    await AsyncStorage.setItem(key, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Failed to set cache:', error);
  }
};

/**
 * キャッシュからデータを取得
 */
export const getCache = async <T>(
  key: string,
  maxAge: number
): Promise<T | null> => {
  try {
    const cachedString = await AsyncStorage.getItem(key);
    if (!cachedString) return null;

    const cached: CacheData<T> = JSON.parse(cachedString);
    const age = Date.now() - cached.timestamp;

    // キャッシュが有効期限内かチェック
    if (age < maxAge) {
      return cached.data;
    }

    // 期限切れの場合は削除
    await AsyncStorage.removeItem(key);
    return null;
  } catch (error) {
    console.error('Failed to get cache:', error);
    return null;
  }
};

/**
 * 特定のキャッシュを削除
 */
export const removeCache = async (key: string): Promise<void> => {
  try {
    await AsyncStorage.removeItem(key);
  } catch (error) {
    console.error('Failed to remove cache:', error);
  }
};

/**
 * パターンに一致するキャッシュを削除
 */
export const removeCacheByPattern = async (pattern: string): Promise<void> => {
  try {
    const keys = await AsyncStorage.getAllKeys();
    const matchingKeys = keys.filter(key => key.startsWith(pattern));
    await AsyncStorage.multiRemove(matchingKeys);
  } catch (error) {
    console.error('Failed to remove cache by pattern:', error);
  }
};

/**
 * すべてのキャッシュをクリア
 */
export const clearAllCache = async (): Promise<void> => {
  try {
    const keys = await AsyncStorage.getAllKeys();
    const cacheKeys = keys.filter(key => key.startsWith('cache_'));
    await AsyncStorage.multiRemove(cacheKeys);
  } catch (error) {
    console.error('Failed to clear all cache:', error);
  }
};

// ヘルパー関数
export const getCacheKey = {
  videoFeed: (feedId: string, cursor?: string) =>
    `${CACHE_KEYS.VIDEO_FEED}${feedId}_${cursor || 'initial'}`,
  videoPost: (videoId: string) => `${CACHE_KEYS.VIDEO_POST}${videoId}`,
  profile: (did: string) => `${CACHE_KEYS.PROFILE}${did}`,
  postThread: (uri: string) => `${CACHE_KEYS.POST_THREAD}${uri}`,
};

export { CACHE_DURATION };
